import { PlusCircleIcon, TagIcon, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { subtleFormClass } from "@/styles/ClassOverrides";
import { Show } from "@/components/core";
import { DataType } from "@/entities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { dateFormats } from "@/utils";
import { GetColumnsFromFile } from "./GetColumnsFromFileButton";
import { ColumnList } from "./ColumnList";
import { useAddOrUpdateTemplateController } from "./useAddOrUpdateTemplateController";

export interface AddOrUpdateTemplateProps {
  context: string;
  templateId?: number;
}

export const AddOrUpdateTemplate = (props: AddOrUpdateTemplateProps) => {
  const controller = useAddOrUpdateTemplateController(props);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {controller.new ? "Create New Template" : "Edit Template"}
        </CardTitle>
        <CardDescription>
          Define the structure for your import data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* template details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name</Label>
            <Input
              id="template-name"
              value={controller.template.name}
              onChange={(e) => {
                controller.updateName(e.target.value);
              }}
              placeholder="e.g., Product Import"
              required
              className={subtleFormClass}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="entity-type">Entity Type</Label>
            <Select
              value={controller.template.entityType}
              onValueChange={(value) => {
                controller.updateEntityType(value);
              }}
              disabled={!controller.new}
            >
              <SelectTrigger
                id="entity-type"
                className={cn("w-[180px]", subtleFormClass)}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {controller.entityTypeNames.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="template-description">Description</Label>
          <Textarea
            id="template-description"
            value={controller.template.description}
            onChange={(e) => {
              controller.updateDescription(e.target.value);
            }}
            placeholder="What kind of data will this template import?"
            rows={2}
            className={subtleFormClass}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Label>Tags</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => controller.addTag()}
                className="h-7"
              >
                <PlusCircleIcon className="h-3.5 w-3.5 mr-1" />
                Add Tag
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Show if={controller.tags.length === 0}>
              <p className="text-sm text-muted-foreground">
                No tags added. Click "Add Tag" to create tags.
              </p>
            </Show>
            <Show if={controller.tags.length > 0}>
              {controller.tags.map((tag, index) => (
                <div key={index} className="relative flex items-center">
                  <div
                    className={cn(
                      "flex items-center rounded-md border",
                      subtleFormClass
                    )}
                  >
                    <TagIcon className="h-4 w-4 ml-2 text-muted-foreground" />
                    <Input
                      value={tag}
                      onChange={(e) =>
                        controller.updateTag(index, e.target.value)
                      }
                      className="border-0 h-8 w-[100px] focus-visible:ring-0 pl-1"
                      placeholder="Tag name"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => controller.removeTag(index)}
                      className="h-8 w-8 p-0 hover:bg-transparent hover:text-destructive"
                    >
                      &times;
                    </Button>
                  </div>
                </div>
              ))}
            </Show>
          </div>
        </div>

        <Separator />

        {/* template  fields */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <Label className="text-base font-medium">Field Mapping</Label>

            <GetColumnsFromFile onColumnsExtracted={controller.setColumns} />
          </div>

          <ColumnList
            columns={controller.columns}
            usedColumns={controller.usedColumns}
          />

          <div className="border rounded-md">
            <Table className="[&_th]:px-2 [&_td]:px-2 [&_th]:py-2 [&_td]:py-2">
              <TableHeader>
                <TableRow>
                  <TableHead>From Field</TableHead>
                  <TableHead>To Field</TableHead>
                  <TableHead className="w-[110px]">Type</TableHead>
                  <TableHead className="w-[80px] text-center">
                    Required
                  </TableHead>
                  <TableHead>Precision</TableHead>
                  <TableHead className="w-[230px]">Date Format</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="[&_tr:nth-child(even)]:bg-muted/50">
                {controller.template.fields.map((field, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        value={field.fromField}
                        onChange={(e) => {
                          controller.updateField({
                            ...field,
                            fromField: e.target.value,
                          });
                        }}
                        onDragOver={controller.handleDragOver}
                        onDragLeave={controller.handleDragLeave}
                        onDrop={(e) => controller.handleDrop(e, field)}
                        placeholder={
                          field.required
                            ? "Source Field"
                            : "[Optional] Source Field"
                        }
                        className={cn(
                          "h-8 text-sm",
                          subtleFormClass,
                          controller.isDraggingOver &&
                            "ring-2 ring-primary ring-offset-2"
                        )}
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={field.toField}
                        className={cn(
                          "h-8 text-sm px-3 py-1 flex items-center bg-muted/30 rounded-md text-muted-foreground",
                          subtleFormClass
                        )}
                        disabled={true}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={field.dataType}
                        className={cn(
                          "h-8 text-sm w-[80px] px-3 py-1 flex items-center bg-muted/30 rounded-md text-muted-foreground",
                          subtleFormClass
                        )}
                        disabled={true}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox checked={field.required} disabled={true} />
                    </TableCell>
                    <TableCell>
                      {field.dataType === DataType.decimal ? (
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          value={
                            field.precision !== null ? field.precision : ""
                          }
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (value >= 0 && value <= 10) {
                              controller.updateField({
                                ...field,
                                precision: value,
                              });
                            }
                          }}
                          placeholder="0"
                          className={cn(
                            "h-8 text-sm w-[60px]",
                            subtleFormClass
                          )}
                        />
                      ) : (
                        <Input
                          disabled
                          placeholder="-"
                          className={cn(
                            "h-8 text-sm w-[60px]",
                            subtleFormClass
                          )}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {field.dataType === "date" ||
                      field.dataType === "datetime" ? (
                        <Select
                          value={field.dateFormat || ""}
                          onValueChange={(value) => {
                            controller.updateField({
                              ...field,
                              dateFormat: value,
                            });
                          }}
                        >
                          <SelectTrigger
                            className={cn(
                              "h-8 text-sm w-full",
                              subtleFormClass
                            )}
                          >
                            <SelectValue placeholder="Select format" />
                          </SelectTrigger>
                          <SelectContent>
                            {dateFormats.map((format) => (
                              <SelectItem key={format.key} value={format.key}>
                                <div>
                                  <div className="inline-block font-semibold">
                                    {format.key}
                                  </div>
                                  <div className="inline-block font-light mx-2">
                                    -
                                  </div>
                                  <div className="inline-block font-light">
                                    {format.value}
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          disabled
                          placeholder="-"
                          className={cn("h-8 text-sm w-full", subtleFormClass)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* temp save function - will be removed once the import is wired up (allowing template creation as part of import) */}

        <Separator />

        <Button
          onClick={() => controller.saveTemplate()}
          disabled={controller.isSaving}
        >
          {controller.isSaving && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Save Template
        </Button>
      </CardContent>
    </Card>
  );
};
